import { ActionTree, Commit, MutationTree } from 'vuex';
import { State } from '@/store/modules/global/settings/settings.store';
import { USER_READ_ONLY } from '@/utils/constants';
import { UserService } from '@/services/api/user.service';
import { CustomService } from '@/services/api/custom.service';
import getEnv from '@/utils/env';
import axiosInstance from '@/services/api/http.service.js';
import store from '@/store/index';
import { USER_TYPE_STANDARD } from '@/components/Settings/userAdministration/userCreation.types';
import { usePermissions } from '@/hooks/usePermissions';
import { HOME_RESOURCE_GROUP } from '@/router/constant';

const API_URL =
  getEnv('VUE_APP_ALERT_HOST') ||
  process.env.VUE_APP_ALERT_HOST ||
  'http://localhost:63250';

const http = new axiosInstance({ auth: true });

const actions: ActionTree<State, any> = {
  setUserInfo(context: { commit: Commit }, data: any): void {
    context.commit('setUser', data);
    context.commit('setToken', data);
  },

  getTimeZone(context: { commit: Commit }, data: any): void {
    try {
      UserService.getTimeZone()
        .then((resp) => {
          store.commit('globalState/settings/setCurrentTimezone', resp?.data);
        })
        .catch((error) => error);
    } catch (error) {
      console.log('error', error);
    }
  },
  async updateTimeZone(context: { commit: Commit }, data: any) {
    const timeData = {
      timezoneId: data.value,
    };
    try {
      await UserService.updateTimeZone(timeData);
      const response = await http.post(
        `${API_URL}/api/v1/rules/timezone`,
        timeData,
      );
      if (!(response.data && response.data.success)) {
        throw new Error(response.data.message);
      }
      store.commit('globalState/settings/setUpdatedTimezone', data);
    } catch (error) {
      // Rolling timezone to original value
      timeData.timezoneId = this.state.globalState.settings.timezone;
      await UserService.updateTimeZone(timeData);
      throw error;
    }
  },
  updateDashboard(context: { commit: Commit }, data: any): void {
    const newDashboard = {
      dashboardId: data,
    };
    try {
      UserService.dashboardUpdate(newDashboard)
        .then((resp) => {
          if (resp.data.success === true) {
            context.commit('setDashboard', data);
          }
        })
        .catch((error) => error);
    } catch (error) {
      console.log('error', error);
    }
  },
  getTokenForOapAccess(context: { commit: Commit }, data: any) {
    return new Promise((resolve, reject) => {
      try {
        UserService.getOapAccessToken(data).then((resp) => {
          context.commit('setOapAccessToken', resp.data);
          resolve(resp.data);
        });
      } catch (error) {
        context.commit('setOapAccessToken', {});
      }
    });
  },
  getProfile(context: { commit: Commit }, data: any): void {
    try {
      UserService.getProfile()
        .then(async (resp) => {
          context.commit('setUser', resp.data);
          context.commit('setEmail', resp.data.emailId);
          context.commit(
            'setIsBillingAdmin',
            resp?.data?.isBillingAdmin ?? false,
          );
          context.commit(
            'setUserType',
            resp.data?.userType || USER_TYPE_STANDARD,
          );
          if (
            resp.data.dashboard === undefined ||
            resp.data.dashboard === '' ||
            resp.data.dashboard === null
          ) {
            context.commit('setDashboard', 'default');
            store.commit('homeState/setDefaultDashboard', {
              name: 'Standard',
              id: 'default',
            });
          } else {
            context.commit('setDashboard', resp.data.dashboard);

            if (
              resp.data.dashboard !== 'default' &&
              resp.data.dashboard !== 'defaultBeta'
            ) {
              const { hasCapabilityPermission } = usePermissions();
              const hasHomePermission =
                hasCapabilityPermission(HOME_RESOURCE_GROUP);

              if (hasHomePermission) {
                const { data } = await CustomService.getDashboardTemplate(
                  resp.data.dashboard,
                  false,
                );
                if (data.data[0]?.id) {
                  store.commit('homeState/setDefaultDashboard', {
                    name: data.data[0]?.name,
                    id: data.data[0]?.id,
                  });
                } else {
                  context.commit('setDashboard', 'default');
                  store.commit('homeState/setDefaultDashboard', {
                    name: 'Standard',
                    id: 'default',
                  });
                }
              }
            } else {
              if (resp.data.dashboard === 'default') {
                store.commit('homeState/setDefaultDashboard', {
                  name: 'Standard',
                  id: 'default',
                });
              } else {
                store.commit('homeState/setDefaultDashboard', {
                  name: 'Standard Beta',
                  id: 'defaultBeta',
                  isBeta: true,
                });
              }
            }
          }
          if (resp.data.success) {
            context.commit('setIsAuthenticated', true);
          }
          context.commit('setUserRoleId', resp.data.roleId);
          context.commit('setUserRoleName', resp.data.roleName);
          context.commit(
            'setUserAccess',
            !(resp.data.roleName === USER_READ_ONLY),
          );
        })
        .catch((error) => error);
    } catch (error) {
      console.log('error', error);
    }
  },
  getUserLoginCount(context: { commit: Commit }, data: any): void {
    try {
      UserService.getUserLoginCount()
        .then((resp) => {
          context.commit('setUserLoginCount', resp.data.count);
        })
        .catch((err) => console.log('error', err));
    } catch (error) {
      console.log('error', error);
    }
  },
  getUserLastLogin(context: { commit: Commit }, data: any): void {
    try {
      UserService.getUserLastLogin()
        .then((resp) => {
          context.commit('setUserLastLogin', resp.data.lastLogin);
        })
        .catch((err) => console.log('error', err));
    } catch (error) {
      console.log('error', error);
    }
  },
  logout(context: { commit: Commit }, data: number): void {
    context.commit('setUser', data);
    context.commit('setEmail', data);
    context.commit('setToken', data);
  },
  // logout
};

export default actions;
